/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useLocale } from 'next-intl'
import { Dropdown } from '@hashicorp/mds-react/components'
import type {
	DropdownContentProps,
	DropdownToggleButtonProps,
} from '@hashicorp/mds-react/components'
import {
	SUPPORTED_LOCALES,
	LOCALE_LANGUAGE_MAP,
} from '@web/utils/i18n/constants'
import { usePathname } from 'lib/i18n/routing'
import s from './locale-switcher.module.css'

interface LocaleSwitcherProps {
	/**
	 * Overrides the text on the switcher's dropdown button
	 */
	buttonText?: string
	/**
	 * Provides a list of locales available for switching,
	 * which overrides the `SUPPORTED_LOCALES` list
	 */
	localesAvailable?: string[]
	/**
	 * Moves the dropdown list according to one of the available listPosition values
	 */
	listPosition?: DropdownContentProps['listPosition']
	/**
	 * Sets `primary` or `secondary` styling for the dropdown
	 */
	variant?: DropdownToggleButtonProps['color']
}

export default function LocaleSwitcher({
	buttonText,
	localesAvailable,
	listPosition = 'left',
	variant = 'secondary',
}: LocaleSwitcherProps) {
	let pathname = usePathname()
	const locale = useLocale()

	const localeList =
		localesAvailable?.length > 0 ? localesAvailable : SUPPORTED_LOCALES

	return (
		<Dropdown.Root key={pathname + locale}>
			<Dropdown.ToggleButton
				icon="globe"
				text={buttonText || LOCALE_LANGUAGE_MAP[locale]}
				color={variant}
			/>
			<Dropdown.Content listPosition={listPosition} width="110%">
				<Dropdown.List className={s.localeSwitchList}>
					{localeList.map((supportedLocale) => {
						/**
						 * Re-assign the pathname on the homepage
						 * to prevent duplicated locales there
						 */
						if (pathname === '/') {
							pathname = '/'
						}

						return (
							<Dropdown.ListItem.Interactive
								key={supportedLocale}
								locale={supportedLocale}
								href={pathname}
								text={LOCALE_LANGUAGE_MAP[supportedLocale]}
								trailingIcon={locale === supportedLocale ? 'check' : undefined}
								prefetch={false}
							/>
						)
					})}
				</Dropdown.List>
			</Dropdown.Content>
		</Dropdown.Root>
	)
}
